import { HttpClient, HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import saveAs from "file-saver";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DownloadProgressComponent } from "src/app/shared/download-progress/download-progress.component";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Injectable({
    providedIn: "root"
})
export class ApiDownloadService {
    cancelSubject = new Subject<void>();

    constructor(private httpClient: HttpClient, private snackBar: MatSnackBar, private translate: TranslateService) {
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);
    }

    download(url: string): Observable<Blob> {
        return this.httpClient.get(url, { headers: { "Access-Control-Allow-Origin": "*" }, responseType: "blob" });
    }

    // Recomendado apenas para arquivos pequenos
    downloadWithProgress(url: string, fileName: string): void {
        const snackBarRef = this.snackBar.openFromComponent(DownloadProgressComponent, {
            data: { progress: 0 },
            duration: undefined
        });

        snackBarRef.afterDismissed().subscribe(() => this.cancelDownload());

        this.httpClient
            .get(url, {
                headers: { "Access-Control-Allow-Origin": "*" },
                responseType: "blob",
                reportProgress: true,
                observe: "events"
            })
            .pipe(takeUntil(this.cancelSubject))
            .subscribe({
                next: (event) => {
                    if (event.type === HttpEventType.DownloadProgress) {
                        const progress = Math.round((100 * event.loaded) / (event.total || 1));
                        snackBarRef.instance.progress = progress;
                    }

                    if (event.type === HttpEventType.Response) {
                        snackBarRef.dismiss();

                        const blob = event.body as Blob;

                        if (!this.hasExtension(fileName)) fileName = fileName + this.getFileExtensionFromUrl(url);

                        saveAs(new Blob([blob.slice(0)]), fileName);
                    }
                },
                error: (err) => {
                    snackBarRef.dismiss();
                    this.snackBar.open(this.translate.instant("DOWNLOAD.ERROR"), "OK", {
                        duration: 0,
                        panelClass: "msg-error"
                    });
                }
            });
    }

    cancelDownload(): void {
        this.cancelSubject.next();
        this.cancelSubject.complete();
    }

    getFileExtensionFromUrl(url: string): string | null {
        const fileName = url.split("?")[0].split("/").pop();
        const ext = fileName?.split(".").pop() || null;
        return "." + ext;
    }

    hasExtension(fileName: string): boolean {
        const parts = fileName.split(".");
        return parts.length > 1 && parts[parts.length - 1] !== "";
    }
}
